const COOKIE_BANNER_DISMISS_SELECTORS = [
  "#shopify-pc__banner__btn-accept",
  "#shopify-pc__banner__btn-decline",
  "#shopify-pc__prefs__header-accept",
  "#shopify-pc__prefs__header-decline",
  "#shopify-pc__prefs__header-save",
].join(",");

const CONSENT_UI_IDS = ["shopify-pc__banner", "shopify-pc__prefs"];
const FOCUSABLE_SELECTOR =
  'summary, a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const BACKGROUND_ARIA_HIDDEN_ATTR = "data-dbi-consent-bg-hidden";
const BACKGROUND_TABINDEX_ATTR = "data-dbi-consent-prev-tabindex";
const FOCUS_RETRY_DELAYS = [0, 16, 50, 100, 150, 250, 400];

let pendingFocusAfterDismiss = false;
let focusRetryTimers = [];
let consentTrapActive = false;
let initialFocusContainer = null;
let consentUiWasVisible = false;
let consentDomObserverTimer = null;

function isCookieBannerDismissAction(target) {
  return target instanceof Element && Boolean(target.closest(COOKIE_BANNER_DISMISS_SELECTORS));
}

function isConsentUiVisible() {
  return CONSENT_UI_IDS.some((id) => {
    const element = document.getElementById(id);
    return element && getComputedStyle(element).display !== "none";
  });
}

function getActiveConsentContainer() {
  const prefs = document.getElementById("shopify-pc__prefs");
  if (prefs && getComputedStyle(prefs).display !== "none") return prefs;

  const banner = document.getElementById("shopify-pc__banner");
  if (banner && getComputedStyle(banner).display !== "none") return banner;

  return null;
}

function getConsentFocusables(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => getComputedStyle(element).visibility !== "hidden" && element.getClientRects().length > 0,
  );
}

function getPageTopFocusTarget() {
  return document.querySelector(".skip-to-content-link") || document.getElementById("root");
}

function isFocusOnPageTop(target) {
  const active = document.activeElement;
  if (!active || !target) return false;
  if (active === target) return true;
  return target.contains(active);
}

function cancelPendingFocusRestore() {
  focusRetryTimers.forEach((timer) => clearTimeout(timer));
  focusRetryTimers = [];
}

function focusPageTop() {
  if (consentTrapActive || isConsentUiVisible()) return false;

  const target = getPageTopFocusTarget();
  if (!target) return false;

  window.scrollTo(0, 0);

  try {
    target.focus({ preventScroll: true });
  } catch {
    try {
      target.focus();
    } catch {
      return false;
    }
  }

  return isFocusOnPageTop(target);
}

function restoreFocusToPageTop() {
  if (!pendingFocusAfterDismiss || isConsentUiVisible()) return;

  deactivateConsentTrap();
  cancelPendingFocusRestore();

  const tryFocus = () => {
    if (!pendingFocusAfterDismiss || isConsentUiVisible()) return;

    if (focusPageTop()) {
      pendingFocusAfterDismiss = false;
      cancelPendingFocusRestore();
    }
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(tryFocus);
  });

  FOCUS_RETRY_DELAYS.forEach((delay) => {
    focusRetryTimers.push(setTimeout(tryFocus, delay));
  });
}

function scheduleFocusAfterDismiss() {
  pendingFocusAfterDismiss = true;
  restoreFocusToPageTop();
}

function handleConsentUiChange() {
  syncConsentAccessibility();

  if (!pendingFocusAfterDismiss || isConsentUiVisible()) return;

  restoreFocusToPageTop();
}

function enhanceConsentAccessibility(container) {
  container.setAttribute("aria-modal", "true");

  const policyLink = container.querySelector("#shopify-pc__banner__body-policy-link");
  if (policyLink?.getAttribute("tabindex") === "-1") {
    policyLink.removeAttribute("tabindex");
  }
}

function disableBackgroundAccessibility() {
  const activeContainer = getActiveConsentContainer();

  Array.from(document.body.children).forEach((child) => {
    if (child.tagName === "SCRIPT" || child.tagName === "STYLE") return;
    if (activeContainer && child === activeContainer) return;
    if (child.hasAttribute(BACKGROUND_ARIA_HIDDEN_ATTR)) return;

    child.setAttribute("aria-hidden", "true");
    child.setAttribute(BACKGROUND_ARIA_HIDDEN_ATTR, "true");
  });

  document.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => {
    if (activeContainer?.contains(element) || element.hasAttribute(BACKGROUND_TABINDEX_ATTR)) return;

    element.setAttribute(BACKGROUND_TABINDEX_ATTR, element.getAttribute("tabindex") ?? "");
    element.setAttribute("tabindex", "-1");
  });
}

function restoreBackgroundAccessibility() {
  document.querySelectorAll(`[${BACKGROUND_ARIA_HIDDEN_ATTR}]`).forEach((element) => {
    element.removeAttribute("aria-hidden");
    element.removeAttribute(BACKGROUND_ARIA_HIDDEN_ATTR);
  });

  document.querySelectorAll(`[${BACKGROUND_TABINDEX_ATTR}]`).forEach((element) => {
    const previousTabindex = element.getAttribute(BACKGROUND_TABINDEX_ATTR);

    if (previousTabindex === "") {
      element.removeAttribute("tabindex");
    } else {
      element.setAttribute("tabindex", previousTabindex);
    }

    element.removeAttribute(BACKGROUND_TABINDEX_ATTR);
  });
}

function focusInitialConsentControl(container) {
  const focusables = getConsentFocusables(container);
  if (!focusables.length) return;

  const policyLink = container.querySelector("#shopify-pc__banner__body-policy-link");
  const focusTarget = policyLink && focusables.includes(policyLink) ? policyLink : focusables[0];

  if (!container.contains(document.activeElement)) {
    focusTarget.focus();
  }
}

function activateConsentTrap() {
  const container = getActiveConsentContainer();
  if (!container) {
    deactivateConsentTrap();
    return;
  }

  consentTrapActive = true;
  enhanceConsentAccessibility(container);
  disableBackgroundAccessibility();

  if (initialFocusContainer !== container) {
    initialFocusContainer = container;
    requestAnimationFrame(() => focusInitialConsentControl(container));
  }
}

function deactivateConsentTrap() {
  if (!consentTrapActive && !initialFocusContainer) return;

  consentTrapActive = false;
  initialFocusContainer = null;
  restoreBackgroundAccessibility();
}

function syncConsentAccessibility() {
  const isVisible = isConsentUiVisible();

  if (isVisible === consentUiWasVisible) return;

  consentUiWasVisible = isVisible;

  if (isVisible) {
    activateConsentTrap();
    return;
  }

  deactivateConsentTrap();
}

function redirectFocusToConsentContainer(event) {
  if (!consentTrapActive || pendingFocusAfterDismiss) return;

  const container = getActiveConsentContainer();
  if (!container || container.contains(event.target)) return;

  event.preventDefault();
  event.stopPropagation();

  const focusables = getConsentFocusables(container);
  focusables[0]?.focus({ preventScroll: true });
}

function handleConsentTabKey(event) {
  if (event.key !== "Tab" || !consentTrapActive || pendingFocusAfterDismiss) return;

  const container = getActiveConsentContainer();
  if (!container) return;

  const focusables = getConsentFocusables(container);
  if (!focusables.length) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const activeElement = document.activeElement;

  if (!container.contains(activeElement)) {
    event.preventDefault();
    first.focus();
    return;
  }

  if (!event.shiftKey && activeElement === last) {
    event.preventDefault();
    first.focus();
    return;
  }

  if (event.shiftKey && activeElement === first) {
    event.preventDefault();
    last.focus();
  }
}

function observeConsentUi(element) {
  if (!element || element.dataset.dbiConsentFocusObserved === "true") return;

  element.dataset.dbiConsentFocusObserved = "true";

  const observer = new MutationObserver(() => {
    handleConsentUiChange();
  });

  observer.observe(element, { attributes: true, attributeFilter: ["style", "class", "hidden"] });
}

function observeConsentUiElements() {
  CONSENT_UI_IDS.forEach((id) => {
    observeConsentUi(document.getElementById(id));
  });
}

function initCookieConsentFocus() {
  document.addEventListener(
    "click",
    (event) => {
      if (!isCookieBannerDismissAction(event.target)) return;
      scheduleFocusAfterDismiss();
    },
    true,
  );

  document.addEventListener("visitorConsentCollected", scheduleFocusAfterDismiss);
  document.addEventListener("focusin", redirectFocusToConsentContainer, true);
  document.addEventListener("keydown", handleConsentTabKey, true);

  const scheduleConsentDomCheck = () => {
    clearTimeout(consentDomObserverTimer);
    consentDomObserverTimer = setTimeout(() => {
      observeConsentUiElements();
      handleConsentUiChange();
    }, 100);
  };

  const domObserver = new MutationObserver(scheduleConsentDomCheck);

  domObserver.observe(document.body, { childList: true });
  observeConsentUiElements();
  syncConsentAccessibility();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initCookieConsentFocus);
} else {
  initCookieConsentFocus();
}
